import { Link, Outlet, useLocation } from "@tanstack/react-router";
import { SignInButton, SignedIn, SignedOut, UserButton, useUser } from "@clerk/clerk-react";
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import {
  Archive,
  Bell,
  BellRing,
  BookOpen,
  LayoutDashboard,
  LogIn,
  Users,
} from "lucide-react";
import { api } from "../../../convex/_generated/api";
import { ContentWorkflowProvider } from "../../contentWorkflowContext";
import { LifecycleShellProvider } from "../../lifecycleShellContext";
import { lifecycleMonthSubscriptionScope, resolveLifecycleActorRole, type LifecycleActorRole } from "../../lifecycleWorkspace";
import { isConfigured, serviceConfig } from "../../config";
import { productName } from "../../productIdentity";
import { isPublicReviewPath } from "../../publicReviewPath";
import { isValidProposalMonth } from "../../calendarUiModel";
import { canManageWorkspaceActions, hasActiveMembership } from "../../workspaceRolePermissions";
import { ConvexAuthGate, WorkspaceAccessWaiting } from "../auth";
import { UnauthenticatedLandingPage } from "../landing/UnauthenticatedLandingPage";
import { clientDisplayNameFromSlug } from "../shared/clientDisplayName";
import { formatProposalMonth } from "../../monthlyProposalModel";
import { ProposalStageNav, type ProposalStageSummary } from "./ProposalStageNav";
import { LifecycleBreadcrumbs } from "../shared/LifecycleStageSurfaces";
import { applyAgencyFavicon, genericAgencyBranding, normalizeAgencyBranding, type AgencyBranding } from "../../agencyBranding";
export { getProposalStageReached } from "./ProposalStageNav";

/** Route segments are not guaranteed to be a month, and Intl throws on an
 *  invalid date, so only format what actually looks like YYYY-MM. */
function shellMonthLabel(segment: string) {
  return isValidProposalMonth(segment) ? formatProposalMonth(segment) : segment;
}

export function getShellCopy(pathname: string) {
  const clientMonthMatch = pathname.match(/^\/client\/([^/]+)\/([^/]+)/);
  const clientMatch = pathname.match(/^\/client\/([^/]+)\/?$/);
  if (pathname === "/") {
    return { eyebrow: "", title: "" };
  }
  if (pathname === "/guide") {
    return { eyebrow: "", title: "" };
  }
  if (clientMatch) {
    // Client Home prints the client name as its own page heading, so the shell
    // stays silent rather than stating the same month/client a second time.
    return { eyebrow: "", title: "" };
  }
  if (pathname === "/company") {
    return {
      eyebrow: "Client Home",
      title: "Client Home",
    };
  }
  if (pathname === "/clients") {
    return { eyebrow: "", title: "" };
  }
  // Admin and Team access print their own page heading, so the shell stays
  // silent rather than stating the same surface a second time.
  if (pathname === "/admin" || pathname === "/admin/team" || pathname === "/admin/clients") {
    return { eyebrow: "", title: "" };
  }
  if (pathname === "/admin/calendars") {
    return { eyebrow: "", title: "" };
  }
  if (pathname.endsWith("/defaults")) {
    return { eyebrow: "", title: "" };
  }
  if (pathname.endsWith("/proposal-configuration")) {
    return { eyebrow: "", title: "" };
  }
  const ownsStageHeading = /^\/client\/[^/]+\/[^/]+\/(?:calendar|briefing|design|client-review)\/?$/.test(pathname)
    || ["/workspace", "/briefing", "/design", "/client-review"].includes(pathname);
  if (ownsStageHeading) {
    return { eyebrow: "", title: "" };
  }
  if (pathname.startsWith("/client-review/") || pathname.startsWith("/delivery-asset/")) {
    return {
      eyebrow: "Client Review",
      title: "Shared review link",
    };
  }
  if (clientMonthMatch) {
    return {
      eyebrow: `${clientDisplayNameFromSlug(clientMonthMatch[1])} · ${shellMonthLabel(clientMonthMatch[2])}`,
      title: "Monthly Proposal",
    };
  }
  return {
    eyebrow: productName,
    title: "Workspace",
  };
}

function useShellCopy() {
  return getShellCopy(useLocation().pathname);
}


export function getShellRouteContext(pathname: string): { clientSlug?: string; month?: string } {
  const clientMonthMatch = pathname.match(/^\/client\/([^/]+)\/([^/]+)/);
  if (clientMonthMatch && isValidProposalMonth(clientMonthMatch[2])) {
    return {
      clientSlug: clientMonthMatch[1],
      month: clientMonthMatch[2],
    };
  }

  const clientMatch = pathname.match(/^\/client\/([^/]+)\/?$/);
  if (clientMatch) {
    return {
      clientSlug: clientMatch[1],
    };
  }

  return {};
}

export function getLifecycleReadStage(pathname: string): "ideation" | "workbench" | undefined {
  const match = pathname.match(/^\/client\/[^/]+\/[^/]+\/(calendar|briefing|design)\/?$/);
  if (match?.[1] === "calendar") return lifecycleMonthSubscriptionScope("ideation");
  if (match?.[1] === "briefing" || match?.[1] === "design") return lifecycleMonthSubscriptionScope(match[1]);
  return undefined;
}

export function getShellNavState(pathname: string): {
  clientHomeParams?: { clientSlug: string };
  stageParams?: { clientSlug: string; month: string };
} {
  const routeContext = getShellRouteContext(pathname);
  return {
    clientHomeParams: routeContext.clientSlug ? { clientSlug: routeContext.clientSlug } : undefined,
    stageParams: routeContext.clientSlug && routeContext.month
      ? { clientSlug: routeContext.clientSlug, month: routeContext.month }
      : undefined,
  };
}

export function Shell() {
  const location = useLocation();
  const clerkEnabled = isConfigured(serviceConfig.clerk.publishableKey);
  const convexEnabled = isConfigured(serviceConfig.convex.url);

  if (isPublicReviewPath(location.pathname)) {
    return (
      <ContentWorkflowProvider>
        <main className={location.pathname.startsWith("/delivery-asset/") ? "review-shell delivery-asset-shell" : "review-shell"}>
          <Outlet />
        </main>
      </ContentWorkflowProvider>
    );
  }

  if (clerkEnabled) {
    return (
      <>
        <SignedOut>
          <UnauthenticatedLandingPage />
        </SignedOut>
        <SignedIn>
          <WorkspaceShell clerkEnabled={clerkEnabled} convexEnabled={convexEnabled} />
        </SignedIn>
      </>
    );
  }

  return <WorkspaceShell clerkEnabled={clerkEnabled} convexEnabled={convexEnabled} />;
}

function WorkspaceShell(props: { clerkEnabled: boolean; convexEnabled: boolean }) {
  return props.convexEnabled ? <ConfiguredWorkspaceShell {...props} /> : <WorkspaceShellView {...props} />;
}

function ConfiguredWorkspaceShell(props: { clerkEnabled: boolean; convexEnabled: boolean }) {
  const location = useLocation();
  const routeContext = getShellRouteContext(location.pathname);
  const lifecycleReadStage = getLifecycleReadStage(location.pathname);
  const upsertCurrentUser = useMutation(api.identity.upsertCurrentUser);
  const currentUser = useQuery(api.identity.getCurrentUserMemberships, {});
  const stageSummary = useQuery(
    lifecycleReadStage ? api.lifecycleWorkspace.getForClientMonth : api.workflowPolicy.getForClientMonth,
    routeContext.clientSlug && routeContext.month
      ? lifecycleReadStage
        ? { clientSlug: routeContext.clientSlug, month: routeContext.month, stage: lifecycleReadStage }
        : { clientSlug: routeContext.clientSlug, month: routeContext.month }
      : "skip",
  ) as ProposalStageSummary | null | undefined;
  useEffect(() => {
    void upsertCurrentUser({}).catch(() => undefined);
  }, [upsertCurrentUser]);
  return <WorkspaceShellView {...props} currentUser={currentUser} stageSummary={stageSummary} />;
}

function isLifecycleActorRole(value: unknown): value is LifecycleActorRole {
  return value === "admin" || value === "am" || value === "designer" || value === "client";
}

function WorkspaceShellView({
  clerkEnabled,
  convexEnabled,
  currentUser,
  proposalStatus,
  stageSummary,
}: {
  clerkEnabled: boolean;
  convexEnabled: boolean;
  currentUser?: { memberships?: any[]; accessRequestState?: "pending" | "ambiguous" | "unclaimable"; branding?: AgencyBranding };
  proposalStatus?: string;
  stageSummary?: ProposalStageSummary | null;
}) {
  const location = useLocation();
  const shellCopy = useShellCopy();
  const shellNavState = getShellNavState(location.pathname);
  const showSetupDiagnostics = !import.meta.env.PROD || !clerkEnabled || !convexEnabled;
  const branding = normalizeAgencyBranding(currentUser?.branding ?? genericAgencyBranding);
  useEffect(() => {
    applyAgencyFavicon(branding);
  }, [branding.displayName, branding.logoAssetRef, branding.logoAltText]);
  const canViewAdminNav = !convexEnabled || canManageWorkspaceActions({ memberships: currentUser?.memberships });
  const teamManageOrganizationId = currentUser?.memberships?.find((membership: any) =>
    membership.status === "active" && membership.roles?.includes("agency_admin") && membership.organization?.id,
  )?.organization?.id;
  const shouldGateWorkspace = clerkEnabled && convexEnabled && location.pathname !== "/__qa/provision";
  if (shouldGateWorkspace && currentUser === undefined) {
    return <main className="page-grid"><div className="generic-product-mark" aria-label={genericAgencyBranding.displayName}><span className="brand-mark">CP</span><span>{genericAgencyBranding.displayName}</span></div><div className="empty-workflow-state">Loading workspace access.</div></main>;
  }
  if (shouldGateWorkspace && !hasActiveMembership(currentUser?.memberships)) {
    const suspended = Boolean(currentUser?.memberships?.length && currentUser.memberships.every((membership: any) => membership.status === "suspended"));
    return <WorkspaceAccessWaiting suspended={suspended} routingFailed={currentUser?.accessRequestState === "ambiguous" || currentUser?.accessRequestState === "unclaimable"} />;
  }
  // The policy projection is authorized for this route's company. Never infer
  // preview authority from a role held for some other company membership.
  const policyActorRole = stageSummary?.viewer?.actorRole;
  const actorRole = isLifecycleActorRole(policyActorRole)
    ? policyActorRole
    : shellNavState.stageParams ? undefined : currentUser?.memberships?.length
      ? resolveLifecycleActorRole(currentUser.memberships.flatMap((membership: any) => membership.roles ?? []))
      : undefined;

  return (
    <ContentWorkflowProvider>
      <LifecycleShellProvider actorRole={actorRole} stageSummary={stageSummary}>
      <div className="app-shell">
        <main>
          <header className="global-header">
            <Link to="/" className="global-brand" aria-label={`${branding.displayName} dashboard`}>
              {branding.logoAssetRef ? <img className="global-brand-logo" src={branding.logoAssetRef} alt={branding.logoAltText || `${branding.displayName} logo`} /> : <span className="brand-mark">CP</span>}
              <span>{branding.displayName}</span>
            </Link>
            <nav className="global-nav" aria-label="Global navigation">
              <NavLink to="/" icon={<LayoutDashboard size={17} />} label="Dashboard" />
              <NavLink to="/guide" icon={<BookOpen size={17} />} label="User Guide" />
              <NavLink to="/clients" icon={<Users size={17} />} label="Clients" />
              {canViewAdminNav
                ? teamManageOrganizationId
                  ? <AdminNavLink organizationId={teamManageOrganizationId} />
                  : <NavLink to="/admin" icon={<Archive size={17} />} label="Admin" />
                : null}
            </nav>
            <div className="global-context">
              {shellCopy.eyebrow ? <p className="eyebrow">{shellCopy.eyebrow}</p> : null}
              {shellCopy.title ? <h1>{shellCopy.title}</h1> : null}
            </div>
            <div className="global-actions">
              {convexEnabled ? <ConfiguredNotificationBell /> : <NotificationBell notifications={[]} unreadCount={0} onRead={() => undefined} onReadAll={() => undefined} />}
              <AccountAffordance clerkEnabled={clerkEnabled} convexEnabled={convexEnabled} />
            </div>
          </header>
          {shellNavState.stageParams ? <><LifecycleBreadcrumbs {...shellNavState.stageParams} /><ProposalStageNav params={shellNavState.stageParams} pathname={location.pathname} proposalStatus={proposalStatus} stageSummary={stageSummary} stagePolicyLoading={stageSummary === undefined} /></> : null}
          {showSetupDiagnostics && !clerkEnabled ? <SetupDiagnosticsCard clerkEnabled={clerkEnabled} convexEnabled={convexEnabled} /> : null}
          {convexEnabled && clerkEnabled ? (
            <ConvexAuthGate title="Workspace Access">
              <Outlet />
            </ConvexAuthGate>
          ) : (
            <Outlet />
          )}
        </main>
      </div>
      </LifecycleShellProvider>
    </ContentWorkflowProvider>
  );
}

function ConfiguredNotificationBell() {
  const notificationData = useQuery(api.notifications.listForCurrentUser, {}) as {
    notifications?: Array<{ _id: string; title: string; body: string; targetUrl?: string; readAt?: number; createdAt: number }>;
    unreadCount?: number;
  } | null | undefined;
  const markNotificationRead = useMutation(api.notifications.markRead);
  const markAllNotificationsRead = useMutation(api.notifications.markAllRead);
  return <NotificationBell
    notifications={notificationData?.notifications ?? []}
    unreadCount={notificationData?.unreadCount ?? 0}
    onRead={(notificationId) => void markNotificationRead({ notificationId: notificationId as any })}
    onReadAll={() => void markAllNotificationsRead({})}
  />;
}

function NotificationBell({
  notifications,
  unreadCount,
  onRead,
  onReadAll,
}: {
  notifications: Array<{ _id: string; title: string; body: string; targetUrl?: string; readAt?: number; createdAt: number }>;
  unreadCount: number;
  onRead: (notificationId: string) => void;
  onReadAll: () => void;
}) {
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return undefined;
    const closeOnOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => { if (event.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", closeOnOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => { document.removeEventListener("mousedown", closeOnOutside); document.removeEventListener("keydown", closeOnEscape); };
  }, [open]);
  return <div ref={panelRef} className="notification-bell-wrap">
    <button type="button" className="notification-bell" aria-label={`Notifications${unreadCount ? `, ${unreadCount} unread` : ""}`} aria-expanded={open} onClick={() => setOpen((current) => !current)}>
      {unreadCount ? <BellRing size={18} aria-hidden="true" /> : <Bell size={18} aria-hidden="true" />}
      {unreadCount ? <span className="notification-count" aria-hidden="true">{unreadCount > 99 ? "99+" : unreadCount}</span> : null}
    </button>
    {open ? <section className="notification-panel" aria-label="Notifications">
      <header><div><p className="eyebrow">Workspace activity</p><h2>Notifications</h2></div>{unreadCount ? <button type="button" className="button-link secondary" onClick={onReadAll}>Mark all read</button> : null}</header>
      <div className="notification-list">
        {notifications.length ? notifications.map((notification) => <a key={notification._id} className={`notification-entry${notification.readAt ? " is-read" : ""}`} href={notification.targetUrl || "/"} onClick={() => { onRead(notification._id); setOpen(false); }}><span className="notification-entry-title">{notification.title}</span><span>{notification.body}</span></a>) : <p className="empty-workflow-state">No notifications yet.</p>}
      </div>
    </section> : null}
  </div>;
}

function SetupDiagnosticsCard(props: { clerkEnabled: boolean; convexEnabled: boolean }) {
  const ready = props.clerkEnabled && props.convexEnabled;
  return (
    <div className="service-card">
      <span className={ready ? "status-dot online" : "status-dot"} />
      <div>
        <strong>{ready ? "Workspace services ready" : "Local setup mode"}</strong>
        <span>{ready ? "Sign in to continue" : "Using local demo data"}</span>
      </div>
    </div>
  );
}

function AccountAffordance(props: { clerkEnabled: boolean; convexEnabled: boolean }) {
  if (!props.clerkEnabled) {
    return (
      <div className="account-chip demo-account" aria-label="Demo account">
        <span className="account-avatar">D</span>
        <span className="account-copy">
          <strong>Demo mode</strong>
          <span>Local workspace state</span>
        </span>
      </div>
    );
  }

  return (
    <div className="account-area">
      <SignedOut>
        <SignInButton mode="modal">
          <button className="account-signin" type="button">
            <LogIn size={16} />
            Sign in
          </button>
        </SignInButton>
      </SignedOut>
      <SignedIn>
        <SignedInAccount convexEnabled={props.convexEnabled} />
      </SignedIn>
    </div>
  );
}

/**
 * Clerk renders its own avatar, so the only way to keep it inside World B is to
 * pass an appearance rather than fight its generated class names. The world has
 * no radii, and a circular avatar on a hard-edged rail is the one shape that
 * gives away that this control came from somewhere else.
 */
const userButtonAppearance = {
  elements: {
    avatarBox: { width: "30px", height: "30px", borderRadius: "0" },
    userButtonAvatarBox: { width: "30px", height: "30px", borderRadius: "0" },
    userButtonAvatarImage: { borderRadius: "0" },
    userButtonTrigger: { borderRadius: "0" },
  },
} as const;

function SignedInAccount(props: { convexEnabled: boolean }) {
  const { user } = useUser();
  const displayName = user?.fullName ?? user?.primaryEmailAddress?.emailAddress ?? "Signed in";

  return (
    <div className="account-chip">
      <UserButton appearance={userButtonAppearance} />
      <span className="account-copy">
        <strong>{displayName}</strong>
        {props.convexEnabled ? <MembershipSummary /> : <span>Signed in, demo data</span>}
      </span>
    </div>
  );
}

function MembershipSummary() {
  const currentUser = useQuery(api.identity.getCurrentUserMemberships, {});

  const membership = currentUser?.memberships?.[0];
  if (membership) {
    const scope = membership.clientAccess.kind === "all" ? normalizeAgencyBranding(currentUser?.branding ?? genericAgencyBranding).displayName : `${membership.companies.length} clients`;
    return <span>{membership.roles.map(formatRoleLabel).join(" + ")} · {scope}</span>;
  }

  return <span>Checking workspace role</span>;
}

function formatRoleLabel(roleName: string) {
  return roleName
    .split("_")
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(" ");
}

export function NavLink(props: { to: string; icon: JSX.Element; label: string; badgeCount?: number }) {
  const accessibleLabel = props.badgeCount
    ? `${props.label}, ${props.badgeCount} pending access ${props.badgeCount === 1 ? "request" : "requests"}`
    : props.label;
  return (
    <Link to={props.to as never} aria-label={accessibleLabel} activeProps={{ className: "active" }}>
      <span className="global-nav-icon" aria-hidden="true">{props.icon}</span>
      <span className="global-nav-label">{props.label}</span>
      {props.badgeCount ? <span className="global-nav-badge" aria-hidden="true">{props.badgeCount}</span> : null}
    </Link>
  );
}

function AdminNavLink({ organizationId }: { organizationId: string }) {
  const pendingCount = useQuery(api.rbac.countPendingAccess, { organizationId: organizationId as any });
  return <NavLink to="/admin" icon={<Archive size={17} />} label="Admin" badgeCount={pendingCount ?? 0} />;
}
